import { Device, DeviceId, DeviceInfo, DevicePlugin } from '@capacitor/device';
import { ObjectIdService } from '../Objectid/objectId.service';
import { UsersService } from '../../API/users/users.service';
import { MobileDevices } from '../../..';
import { V1_TOKEN_PREFIX } from '../PushNotifications/push-notifications.service';

export class UserDevicesService {
  Device: DevicePlugin;

  constructor(
    private profileService,
    private usersService: UsersService,
    private objectIdService: ObjectIdService
  ) {
    'ngInject';

    this.Device = Device;
  }

  /**
   * Create a user device entity object
   * @param deviceInfo - Platform device
   * @param mobileToken - Push notification token
   * @returns Mobile entity
   */
  createUserDeviceEntity(
    deviceInfo: DeviceInfo,
    deviceId: DeviceId,
    mobileToken: string,
    tokenVersion: string
  ): MobileDevices {
    return {
      _id: this.objectIdService.create(),
      idfi: deviceId.identifier,
      token: mobileToken,
      target: (deviceInfo.platform || '').toLowerCase(),
      device: deviceInfo.model,
      hasToSend: true,
      tokenVersion: tokenVersion ? tokenVersion : 'legacy',
    };
  }

  /**
   * Delete the current device of the collection
   * @param mobileDevices - Mobile devices available
   * @returns Mobile devices clean
   */
  deleteDevice(deviceId: DeviceId, mobileDevices: MobileDevices[] = []) {
    return mobileDevices.filter((userDevice) => {
      return !userDevice.token || deviceId.identifier !== userDevice.idfi;
    });
  }

  /**
   * Get the current devices from the user mobile devices
   * @returns Devices related to the current device
   */
  getMobileDevices() {
    return this.profileService.getProfile().then((profile) => {
      const userDevices = profile.contents.mobileDevices || [];

      return this.Device.getId().then((devideId) =>
        userDevices.filter(
          (userDevice) => devideId.identifier === userDevice.idfi
        )
      );
    });
  }

  /**
   * Add the mobile devices to the current user
   * @param mobileToken - Push notification token
   * @returns User entity
   */
  addMobileDevice(mobileToken: string) {
    return this.profileService.getProfile().then(
      (profile) => {
        let tokenVersion = 'legacy';
        if (mobileToken && mobileToken.startsWith(V1_TOKEN_PREFIX)) {
          tokenVersion = 'v1';
          mobileToken = mobileToken.substring(V1_TOKEN_PREFIX.length);
        }

        return Promise.all([this.Device.getInfo(), this.Device.getId()]).then(
          ([deviceInfo, deviceId]) => {
            const newMobileDevice = this.createUserDeviceEntity(
              deviceInfo,
              deviceId,
              mobileToken,
              tokenVersion
            );
            return this.usersService.addUserMobileDevices(
              profile,
              newMobileDevice
            );
          }
        );
      },

      () => null // supress getProfile errors when user is not logged in
    );
  }

  /**
   * Delete the mobile devices to the current user
   */
  deleteMobileDevice() {
    return this.profileService.getProfile().then((profile) => {
      const mobileDevices: MobileDevices[] = profile.contents.mobileDevices;

      return this.Device.getId().then((deviceId) => {
        const newMobileDevices = this.deleteDevice(deviceId, mobileDevices);

        return this.usersService.updateUserMobileDevices(
          profile,
          newMobileDevices
        );
      });
    });
  }
}
